import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTooltip } from '../components/TooltipSystem';
import {
  ShoppingBag, Plus, ArrowLeft, Trash2, Edit2, Package, Euro,
  Star, Eye, CheckCircle2, XCircle, Clock, Truck, Image as ImageIcon,
  RefreshCw, EyeOff, Gift, Zap, Link2
} from 'lucide-react';

const STATUS_MAP = {
  pending_brand_approval: { label: 'In attesa approvazione', color: '#F59E0B', bg: 'bg-amber-50' },
  pending_superadmin_approval: { label: 'In attesa Lazo', color: '#3B82F6', bg: 'bg-blue-50' },
  approved_by_brand: { label: 'Approvato', color: '#10B981', bg: 'bg-emerald-50' },
  approved: { label: 'Confermato', color: '#10B981', bg: 'bg-emerald-50' },
  sent_to_printful: { label: 'In produzione', color: '#8B5CF6', bg: 'bg-violet-50' },
  in_production: { label: 'In stampa', color: '#8B5CF6', bg: 'bg-violet-50' },
  shipped: { label: 'Spedito', color: '#06B6D4', bg: 'bg-cyan-50' },
  ready_at_venue: { label: 'Pronto al locale', color: '#6366F1', bg: 'bg-indigo-50' },
  delivered: { label: 'Consegnato', color: '#10B981', bg: 'bg-emerald-50' },
  cancelled: { label: 'Cancellato', color: '#EF4444', bg: 'bg-red-50' },
  rejected: { label: 'Rifiutato', color: '#EF4444', bg: 'bg-red-50' },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status, color: '#64748B', bg: 'bg-slate-50' };
  return <span className={`${s.bg} rounded-full px-2.5 py-1 text-[10px] font-bold border`} style={{ color: s.color, borderColor: `${s.color}30` }}>{s.label}</span>;
}

/* ── Product Form ── */
function ProductForm({ product, onSave, onCancel }) {
  const [form, setForm] = useState(product || {
    name: '', description: '', category: 'merchandise', design_url: '',
    price_eur: 0, price_points: 0, cost_eur: 0, trophy_id: '', active: true,
    reward_enabled: false, reward_config: {},
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updReward = (k, v) => setForm(f => ({ ...f, reward_config: { ...f.reward_config, [k]: v } }));

  return (
    <div className="space-y-6">
      <button onClick={onCancel} className="flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#1A1A1A] transition-colors">
        <ArrowLeft size={16} /> Annulla
      </button>
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm space-y-5">
        <h3 className="text-lg font-semibold text-[#1A1A1A]">{product ? 'Modifica prodotto' : 'Nuovo prodotto'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Nome</label>
            <input value={form.name} onChange={e => upd('name', e.target.value)} data-testid="pod-product-name"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Categoria</label>
            <select value={form.category} onChange={e => upd('category', e.target.value)} data-testid="pod-product-category"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none">
              <option value="merchandise">Merchandising</option>
              <option value="trophy_merch">Trofeo Fisico</option>
              <option value="promo">Materiale Promozionale</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Descrizione</label>
          <textarea value={form.description} onChange={e => upd('description', e.target.value)} rows={2} data-testid="pod-product-desc"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-[#D44E20] focus:outline-none resize-none" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">URL Design/Immagine</label>
          <input value={form.design_url} onChange={e => upd('design_url', e.target.value)} placeholder="https://..." data-testid="pod-product-design"
            className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
          {form.design_url && <img src={form.design_url} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Prezzo EUR</label>
            <input type="number" step="0.01" value={form.price_eur} onChange={e => upd('price_eur', parseFloat(e.target.value) || 0)} data-testid="pod-product-price-eur"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Prezzo Punti</label>
            <input type="number" value={form.price_points} onChange={e => upd('price_points', parseInt(e.target.value) || 0)} data-testid="pod-product-price-pts"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Costo (Printful)</label>
            <input type="number" step="0.01" value={form.cost_eur} onChange={e => upd('cost_eur', parseFloat(e.target.value) || 0)} data-testid="pod-product-cost"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
          </div>
        </div>
        {form.category === 'trophy_merch' && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">ID Trofeo collegato</label>
            <input value={form.trophy_id || ''} onChange={e => upd('trophy_id', e.target.value)} placeholder="ID del trofeo" data-testid="pod-product-trophy"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
          </div>
        )}

        {/* Reward toggle */}
        <div className="border border-slate-200/60 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gift size={16} className="text-[#D44E20]" />
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">Usa come ricompensa</p>
                <p className="text-[10px] text-[#64748B]">Rendi questo prodotto disponibile come premio per challenge, coupon, giochi</p>
              </div>
            </div>
            <button onClick={() => upd('reward_enabled', !form.reward_enabled)} data-testid="reward-toggle"
              className={`w-11 h-6 rounded-full relative transition-colors ${form.reward_enabled ? 'bg-[#D44E20]' : 'bg-slate-200'}`}>
              <div className={`w-5 h-5 rounded-full bg-white shadow absolute top-0.5 transition-all ${form.reward_enabled ? 'left-5.5' : 'left-0.5'}`} style={{ left: form.reward_enabled ? '22px' : '2px' }} />
            </button>
          </div>
          {form.reward_enabled && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#64748B] mb-1">Tipo ricompensa</label>
                <select value={form.reward_config.type || ''} onChange={e => updReward('type', e.target.value)} data-testid="reward-type-select"
                  className="w-full h-9 bg-slate-50 border border-slate-200 rounded-lg px-2.5 text-xs focus:border-[#D44E20] focus:outline-none">
                  <option value="">Seleziona...</option>
                  <option value="challenge_prize">Premio challenge</option>
                  <option value="coupon_prize">Premio coupon</option>
                  <option value="game_prize">Premio gioco</option>
                  <option value="tier_benefit">Benefit tier</option>
                  <option value="points_redeem">Riscatto punti</option>
                </select>
              </div>
              {form.reward_config.type === 'points_redeem' && (
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#64748B] mb-1">Punti richiesti</label>
                  <input type="number" value={form.reward_config.points_cost || 0} onChange={e => updReward('points_cost', parseInt(e.target.value) || 0)}
                    data-testid="reward-points-cost"
                    className="w-full h-9 bg-slate-50 border border-slate-200 rounded-lg px-2.5 text-xs focus:border-[#D44E20] focus:outline-none" />
                </div>
              )}
              {['challenge_prize', 'coupon_prize'].includes(form.reward_config.type) && (
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#64748B] mb-1">ID collegato (opzionale)</label>
                  <input value={form.reward_config.linked_id || ''} onChange={e => updReward('linked_id', e.target.value)}
                    placeholder="ID challenge o coupon"
                    className="w-full h-9 bg-slate-50 border border-slate-200 rounded-lg px-2.5 text-xs focus:border-[#D44E20] focus:outline-none" />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onCancel} className="px-5 py-2.5 rounded-xl text-sm font-medium text-[#64748B] border border-slate-200 hover:bg-slate-50">Annulla</button>
          <button onClick={async () => { setSaving(true); try { await onSave(form); } finally { setSaving(false); } }} disabled={saving} data-testid="pod-save-product"
            className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] disabled:opacity-50">{saving ? 'Salvataggio...' : product ? 'Salva' : 'Crea prodotto'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function AdminShop() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('products'); // products | orders | create | edit
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('products'); // products | orders
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await adminApi.post('/admin/pod/sync-printful');
      toast.success(`Sincronizzati ${r.data.synced} prodotti da Printful`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore sync'); }
    finally { setSyncing(false); }
  };

  const load = async () => {
    try {
      const [pRes, oRes] = await Promise.all([
        adminApi.get('/admin/pod/products'),
        adminApi.get('/admin/pod/orders'),
      ]);
      setProducts(pRes.data);
      setOrders(oRes.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (data) => {
    await adminApi.post('/admin/pod/products', data);
    toast.success('Prodotto creato!');
    setView('products'); load();
  };
  const handleUpdate = async (data) => {
    await adminApi.put(`/admin/pod/products/${selected.id}`, data);
    toast.success('Prodotto aggiornato!');
    setView('products'); load();
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    await adminApi.delete(`/admin/pod/products/${id}`);
    toast.success('Eliminato'); load();
  };
  const handleApprove = async (id) => {
    await adminApi.post(`/admin/pod/orders/${id}/approve`);
    toast.success('Ordine approvato'); load();
  };
  const handleReject = async (id) => {
    if (!window.confirm('Rifiutare questo ordine?')) return;
    await adminApi.post(`/admin/pod/orders/${id}/reject`, { reason: 'Rifiutato dall\'admin' });
    toast.success('Ordine rifiutato'); load();
  };
  const handleMarkReadyAtVenue = async (id) => {
    if (!window.confirm('Confermi che il merch è arrivato al locale? Il cliente riceverà un\'email.')) return;
    try {
      await adminApi.post(`/admin/pod/orders/${id}/mark-ready-at-venue`);
      toast.success('Cliente notificato 📦'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const handleMarkPickedUp = async (id) => {
    if (!window.confirm('Confermi il ritiro del cliente? L\'ordine sarà chiuso.')) return;
    try {
      await adminApi.post(`/admin/pod/orders/${id}/mark-picked-up`);
      toast.success('Ritiro confermato'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const pendingOrders = orders.filter(o => o.status === 'pending_brand_approval').length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;
  if (view === 'create') return <div className="p-6 max-w-3xl mx-auto"><ProductForm onSave={handleCreate} onCancel={() => setView('products')} /></div>;
  if (view === 'edit' && selected) return <div className="p-6 max-w-3xl mx-auto"><ProductForm product={selected} onSave={handleUpdate} onCancel={() => setView('products')} /></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-shop-page">
      <PageTooltip page="shop" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><ShoppingBag size={22} className="text-[#D44E20]" /> Shop & Merchandising</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestisci prodotti print-on-demand e ordini clienti</p>
        </div>
        {tab === 'products' && (
          <div className="flex gap-2">
            <button onClick={handleSync} disabled={syncing} data-testid="sync-printful-btn"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-[#64748B] border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50">
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Sync...' : 'Sync Printful'}
            </button>
            <button onClick={() => setView('create')} data-testid="create-pod-product-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] transition-colors">
              <Plus size={16} /> Nuovo prodotto
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-6 max-w-xs">
        <button onClick={() => setTab('products')} data-testid="tab-products"
          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${tab === 'products' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#64748B]'}`}>
          Prodotti ({products.length})
        </button>
        <button onClick={() => setTab('orders')} data-testid="tab-orders"
          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors relative ${tab === 'orders' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#64748B]'}`}>
          Ordini ({orders.length})
          {pendingOrders > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D44E20] text-white rounded-full text-[9px] flex items-center justify-center">{pendingOrders}</span>}
        </button>
      </div>

      {/* Products tab */}
      {tab === 'products' && (
        products.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center shadow-sm">
            <Package size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-[#1A1A1A] font-semibold">Nessun prodotto</p>
            <p className="text-sm text-[#64748B] mt-1">Crea il tuo primo prodotto merchandising</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map(p => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid={`pod-product-${p.id}`}
                className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden hover:shadow-md transition-all">
                {p.design_url ? (
                  <img src={p.design_url} alt="" className="w-full h-36 object-cover" />
                ) : (
                  <div className="w-full h-36 bg-slate-100 flex items-center justify-center"><ImageIcon size={32} className="text-slate-300" /></div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <p className="text-sm font-bold text-[#1A1A1A]">{p.name}</p>
                    <div className="flex gap-1">
                      {p.reward_enabled && <span className="text-[9px] font-bold bg-violet-50 text-violet-600 rounded-full px-2 py-0.5 flex items-center gap-0.5"><Gift size={8} /> Ricompensa</span>}
                      <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${p.active && !p.hidden_by_superadmin ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                        {p.hidden_by_superadmin ? 'Nascosto' : p.active ? 'Attivo' : 'Inattivo'}
                      </span>
                    </div>
                  </div>
                  {p.description && <p className="text-[11px] text-[#64748B] mb-2 line-clamp-2">{p.description}</p>}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-bold text-[#1A1A1A]">{p.price_eur?.toFixed(2)} EUR</span>
                    {p.price_points > 0 && <span className="text-xs text-[#64748B]">o {p.price_points} punti</span>}
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { setSelected(p); setView('edit'); }} data-testid={`edit-pod-${p.id}`}
                      className="flex-1 h-8 rounded-lg text-[11px] font-medium border border-slate-200 text-[#64748B] hover:bg-slate-50 flex items-center justify-center gap-1">
                      <Edit2 size={11} /> Modifica
                    </button>
                    <button onClick={() => handleDelete(p.id)} data-testid={`delete-pod-${p.id}`}
                      className="h-8 w-8 rounded-lg text-red-400 hover:bg-red-50 flex items-center justify-center">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )
      )}

      {/* Orders tab */}
      {tab === 'orders' && (
        orders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center shadow-sm">
            <Truck size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-[#1A1A1A] font-semibold">Nessun ordine</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map(o => (
              <motion.div key={o.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`pod-order-${o.id}`}
                className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-sm font-bold text-[#1A1A1A]">{o.customer_name || 'Cliente'}</p>
                      <StatusBadge status={o.status} />
                      {o.payment_type === 'trophy_free' && <span className="text-[9px] font-bold bg-amber-50 text-amber-600 rounded-full px-2 py-0.5">TROFEO GRATIS</span>}
                      {o.fulfillment === 'venue_pickup' ? (
                        <span className="text-[9px] font-bold bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5" data-testid={`fulfillment-badge-${o.id}`}>
                          📍 RITIRO AL LOCALE{o.pickup_venue_name ? ` · ${o.pickup_venue_name}` : ''}
                        </span>
                      ) : (
                        <span className="text-[9px] font-bold bg-sky-50 text-sky-700 rounded-full px-2 py-0.5" data-testid={`fulfillment-badge-${o.id}`}>
                          🚚 SPEDIZIONE
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#64748B]">
                      {(o.items || []).map(i => `${i.product_name} x${i.quantity}`).join(', ')}
                    </p>
                    <p className="text-[11px] text-[#64748B] mt-0.5">
                      {o.total_eur > 0 && `${o.total_eur.toFixed(2)} EUR`}
                      {o.total_points > 0 && ` + ${o.total_points} punti`}
                      {o.payment_type === 'trophy_free' && 'Gratuito'}
                      {' — '}{new Date(o.created_at).toLocaleDateString('it-IT')}
                    </p>
                    {o.tracking_number && <p className="text-[10px] text-[#3B82F6] mt-1">Tracking: {o.tracking_number}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {o.status === 'pending_brand_approval' && (
                      <div className="flex gap-2">
                        <button onClick={() => handleApprove(o.id)} data-testid={`approve-order-${o.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700">
                          <CheckCircle2 size={12} /> Approva
                        </button>
                        <button onClick={() => handleReject(o.id)} data-testid={`reject-order-${o.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50">
                          <XCircle size={12} /> Rifiuta
                        </button>
                      </div>
                    )}
                    {o.fulfillment === 'venue_pickup' && o.status === 'shipped' && (
                      <button onClick={() => handleMarkReadyAtVenue(o.id)} data-testid={`mark-ready-venue-${o.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700">
                        📦 Arrivato al locale
                      </button>
                    )}
                    {o.fulfillment === 'venue_pickup' && o.status === 'ready_at_venue' && (
                      <button onClick={() => handleMarkPickedUp(o.id)} data-testid={`mark-picked-up-${o.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 size={12} /> Cliente ha ritirato
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
